"use client";

import { type FC, type ReactNode, useEffect, useRef } from "react";
import { motion, type MotionStyle } from "motion/react";
import { mergeClasses, rem } from "@huub-web/utils";
import { useTranslation } from "react-i18next";

import {
  Flex,
  type FlexProps,
  Icon,
  ScrollPane,
  Stack,
  Tooltip,
  usePubSub,
  transitionDurations,
} from "@huub-web/ui";

import { useNavigationState } from "../../providers";
import { useSideContentWidth } from "../../hooks/useSideContentWidth";
import { SIDE_CONTENT_COLLAPSED_WIDTH } from "../../config/layoutConsts";
import { menuNavStyle } from "../Navigation/NavigationMenu.css";

import {
  barStyle,
  containerStyle,
  contentStyle,
  handleStyle,
  lastTrailingStyle,
  scrollPaneContentStyle,
  scrollPaneStyle,
} from "./SideContent.css";

export type SideContentProps = {
  children?: ReactNode;
  leading?: ReactNode;
  trailing?: ReactNode;
  gap?: FlexProps["gap"];
  bar?: ReactNode;
  lastTrailing?: ReactNode;
};

/**
 * SideContent component which can be used to display additional content on the side of layout.
 */
export const SideContent: FC<SideContentProps> = ({
  gap = "m",
  bar,
  children,
  leading,
  trailing,
  lastTrailing,
}) => {
  const pubSub = usePubSub();
  const {
    isSideContentOpen,
    sideContentVariant: variant,
    setSideContentOpen,
  } = useNavigationState();
  const sideContentWidth = useSideContentWidth();
  const { t } = useTranslation();

  useEffect(() => {
    const unsubscribe = pubSub.subscribe("sideContent", ({ action }) => {
      setSideContentOpen(action === "open");
    });
    return () => unsubscribe();
  }, []);

  const collapsedWidth = bar ? SIDE_CONTENT_COLLAPSED_WIDTH : 0;

  const animationInitializedRef = useRef(false);
  const handleAnimationComplete = () => {
    if (!animationInitializedRef.current) {
      animationInitializedRef.current = true;
      return;
    }
    pubSub.publish("recalculateLayout", {});
  };

  return (
    <motion.div
      className={containerStyle({ variant })}
      initial={{ width: collapsedWidth }}
      animate={{
        width: isSideContentOpen ? sideContentWidth : collapsedWidth,
      }}
      exit={{ width: collapsedWidth }}
      transition={{
        duration: transitionDurations.l,
        ease: "easeInOut",
        onComplete: handleAnimationComplete,
      }}
      style={
        {
          "--width": rem(sideContentWidth),
        } as unknown as MotionStyle
      }
    >
      {variant !== "compact" && (
        <Tooltip
          value={t(
            isSideContentOpen ? "sideContent.close" : "sideContent.open",
          )}
          placement="left"
        >
          <button
            type="button"
            className={handleStyle({ open: isSideContentOpen })}
            onClick={() =>
              pubSub.publish("sideContent", {
                action: isSideContentOpen ? "close" : "open",
              })
            }
          >
            <Icon
              icon={isSideContentOpen ? "chevronRight" : "chevronLeft"}
              size="s2"
            />
          </button>
        </Tooltip>
      )}

      <Stack
        overflowHidden
        reveal={{
          animateDelay: transitionDurations.m,
          duration: transitionDurations.l,
        }}
      >
        {isSideContentOpen && (
          <Flex
            className={contentStyle({ variant })}
            direction="column"
            gap={gap}
            shrink={false}
            grow
          >
            {leading}
            {children && (
              <ScrollPane
                overlapColor="navigation"
                overlapSize="s"
                className={scrollPaneStyle}
                contentClassName={scrollPaneContentStyle}
              >
                {children}
              </ScrollPane>
            )}
            {trailing}
            {lastTrailing && (
              <div className={lastTrailingStyle}>{lastTrailing}</div>
            )}
          </Flex>
        )}

        {bar && (
          <div
            className={mergeClasses(
              barStyle,
              menuNavStyle({
                variant: variant === "default" ? "sideContent" : "default",
              }),
            )}
          >
            {!isSideContentOpen && bar}
          </div>
        )}
      </Stack>
    </motion.div>
  );
};
